import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { BigSearchPersonEvent } from "../../api/big-search";
import type { ContractorShowsEntry } from "../../store/reducers/contractorShows";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import {
  eventDetailsState,
  formatEventDates,
  formatVenue,
} from "../../pages/big-search/bigSearchUtils";
import { splitShowsByTime } from "../../utils/methods/contractorShowHistory";

/**
 * "Recent shows worked" block inside the contact card — client request
 * 2026-08-21, off the Contractor List: *"Can we have a link to recent shows
 * worked? Maybe 5"*. Split into what already happened and what is coming up,
 * five each, because on this card the next question after "who is this" is
 * almost always "and are they on anything right now".
 *
 * Rendering rules follow the rest of the card: nothing is printed when there is
 * nothing to say (no "N/A" rows — the popup is already dense), and every colour
 * is explicit. The MUI theme here is the LIGHT default while the tooltip paints
 * dark, so anything inheriting a text colour comes out near-black on near-black.
 */

/** Each bucket scrolls rather than pushing the popup off the screen. */
const BUCKET_MAX_HEIGHT = "8.5rem";

interface ShowRowProps {
  show: BigSearchPersonEvent;
  onOpen: (show: BigSearchPersonEvent) => void;
}

const ShowRow = ({ show, onOpen }: ShowRowProps) => {
  const dates = formatEventDates(show);
  const venue = formatVenue(show);
  const roles = Array.isArray(show?.roles)
    ? show.roles.filter(Boolean).join(", ")
    : "";

  return (
    <Box
      sx={{
        borderLeft: "2px solid rgba(255,255,255,0.25)",
        pl: "0.5rem",
      }}
    >
      <Typography
        component="span"
        role="link"
        tabIndex={0}
        onMouseDown={(e) => {
          // Inside the tooltip portal a real mousedown blurs whatever holds the
          // popover open, which unmounts this node BEFORE the click lands — the
          // row would look dead. Same guard the rest of the card uses.
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen(show);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          e.stopPropagation();
          onOpen(show);
        }}
        sx={{
          display: "block",
          fontSize: "0.8rem",
          lineHeight: 1.35,
          cursor: "pointer",
          color: "#fff",
          textDecoration: "underline",
          textDecorationColor: "rgba(255,255,255,0.35)",
          wordBreak: "break-word",
          "&:hover": { textDecorationColor: "#fff" },
        }}
      >
        {show?.eventName || "(untitled event)"}
        {show?.jobNumber ? ` · #${show.jobNumber}` : ""}
      </Typography>
      <Typography sx={{ fontSize: "0.65rem", opacity: 0.6, lineHeight: 1.3 }}>
        {dates}
        {venue && venue !== "—" ? ` · ${venue}` : ""}
      </Typography>
      {roles ? (
        <Typography sx={{ fontSize: "0.65rem", opacity: 0.6, lineHeight: 1.3 }}>
          {roles}
        </Typography>
      ) : null}
    </Box>
  );
};

interface BucketProps {
  label: string;
  shows: BigSearchPersonEvent[];
  total: number;
  extraLabel: string;
  onOpen: (show: BigSearchPersonEvent) => void;
}

const Bucket = ({ label, shows, total, extraLabel, onOpen }: BucketProps) => {
  if (!shows.length) return null;
  const hidden = Math.max(0, (total ?? shows.length) - shows.length);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 600,
          color: getCssVariableHandler({ variableName: "--primary-pink-hex" }),
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "0.35rem",
          maxHeight: BUCKET_MAX_HEIGHT,
          overflowY: "auto",
          pr: "0.25rem",
        }}
      >
        {shows.map((show) => (
          <ShowRow key={String(show?.eventId)} show={show} onOpen={onOpen} />
        ))}
      </Box>
      {hidden > 0 ? (
        <Typography sx={{ fontSize: "0.65rem", opacity: 0.55 }}>
          +{hidden} {extraLabel}
        </Typography>
      ) : null}
    </Box>
  );
};

export interface ContractorShowsProps {
  /** Cache entry for this person; `undefined` means "not fetched (or failed)". */
  entry?: ContractorShowsEntry;
  loading?: boolean;
}

export const ContractorShows = ({ entry, loading }: ContractorShowsProps) => {
  const navigate = useNavigate();

  const openEvent = (show: BigSearchPersonEvent) => {
    // /event/details takes its id through router STATE, not the URL — an
    // `<a href>` would land on the page with nothing to load. See EventDetails.
    navigate("/event/details", { state: eventDetailsState(show) });
  };

  if (loading && !entry) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <CircularProgress size={12} sx={{ color: "#fff" }} />
        <Typography sx={{ fontSize: "0.7rem", opacity: 0.6 }}>
          Loading shows…
        </Typography>
      </Box>
    );
  }

  // Nothing fetched yet, or the request failed: stay silent. A person with no
  // shows and a person we could not ask about look the same here on purpose —
  // an error line inside a hover tooltip is noise nobody can act on.
  if (!entry) return null;

  const { past, upcoming, pastTotal, upcomingTotal } = splitShowsByTime(
    entry.results,
  );
  if (!past.length && !upcoming.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        width: "100%",
        mt: "0.25rem",
      }}
    >
      <Bucket
        label="Recent shows worked"
        shows={past}
        total={pastTotal}
        extraLabel="earlier"
        onOpen={openEvent}
      />
      <Bucket
        label="Upcoming shows"
        shows={upcoming}
        total={upcomingTotal}
        extraLabel="later"
        onOpen={openEvent}
      />
    </Box>
  );
};

export default ContractorShows;
